import axios from 'axios';
import PQueue from 'p-queue';
import { useToast } from 'vue-toastification';
import { useLoader } from '@/shared/stores/loader.store.js';
import { logout, LogoutReason, refresh } from '#modules/auth/services/AuthService.js';
import i18n from '@/app/lang/i18n.js';
import { BProgress } from '@bprogress/core';
import { useAuthStore } from '#modules/auth/stores/auth.store.js';

const QUEUE_CONCURRENCY = 8;
const QUEUE_INTERVAL_CAP = 8;
const QUEUE_INTERVAL_MS = 400;
const REQUEST_TIMEOUT_MS = 30000; // 30 seconds

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL.replace(/\/$/, ''),
    timeout: REQUEST_TIMEOUT_MS,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
});

let queue = new PQueue({
    concurrency: QUEUE_CONCURRENCY,
    intervalCap: QUEUE_INTERVAL_CAP,
    interval: QUEUE_INTERVAL_MS,
    carryoverConcurrencyCount: true,
});

// URLs that should show the loader
const LOADER_REQUEST_URLS = [
    /\/general\/domains\/register/,
    /\/general\/domains\/transfer/,
    /\/general\/domains\/.*?\/nameservers/,
    /\/general\/ssl-certificates\/request-certificate/,
];

/**
 * Cleanup request state (loader, progress bar)
 *
 * @param {Object} config - Axios request config
 */
function cleanupRequestState(config) {
    const loader = useLoader();

    if (config?.progress) {
        BProgress.done();
    }

    if (config?.showLoader) {
        loader.done();
    }
}

/**
 * Auth request interceptor - adds authorization header and manages request queue
 *
 * @param {import('axios').InternalAxiosRequestConfig} config - Axios request config
 * @returns {Promise<import('axios').InternalAxiosRequestConfig>} Modified request config
 */
function onRequest(config) {
    const loader = useLoader();
    const authStore = useAuthStore();

    // Set the Authorization header for all requests
    if (authStore.accessToken) {
        config.headers['Authorization'] = `Bearer ${authStore.accessToken}`;
    }

    const requestConfig = {
        ...config,
    };

    if (config?.progress) {
        BProgress.start();
    } else if (config?.showLoader && LOADER_REQUEST_URLS.some((regex) => regex.test(config?.url))) {
        loader.pending();
    }

    return new Promise((resolve) => {
        if (config.url && config.url === '/general/oauth/token') {
            queue.pause();
            return resolve(requestConfig);
        }

        queue.add(() => {
            return resolve(requestConfig);
        });
    });
}

/**
 * Handle request error
 *
 * @param {import('axios').AxiosError} error - Axios error object
 * @returns {Promise<never>} Rejected promise
 */
function handleRequestError(error) {
    cleanupRequestState(error.config);
    return Promise.reject(error);
}

/**
 * Handle successful response - cleanup request tracking
 *
 * @param {import('axios').AxiosResponse} response - Axios response
 * @returns {import('axios').AxiosResponse} Original response
 */
const onResponse = (response) => {
    cleanupRequestState(response.config);
    return response;
};

const refreshExpiredTokenClosure = () => {
    let isCalled = false;
    let runningPromise = undefined;

    return () => {
        if (isCalled) {
            return runningPromise;
        }

        isCalled = true;
        runningPromise = refresh({ navigate: true, force: true }).finally(() => {
            // Reset state after refresh completes (success or error)
            isCalled = false;
            runningPromise = undefined;
        });

        return runningPromise;
    };
};

const refreshExpiredToken = refreshExpiredTokenClosure();

/**
 * Handle response errors - token refresh, session expiration, blob parsing
 *
 * @param {import('axios').AxiosError} error - Axios error object
 * @returns {Promise<never>} Rejected promise with error
 */
async function handleResponseError(error) {
    cleanupRequestState(error.config);

    const isCancelled = error.name === 'CanceledError' || error.code === 'ERR_CANCELED';
    const status = error.response?.status;

    // Do not show toast notifications for cancelled requests
    if (isCancelled || !status) {
        return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (originalRequest.url === '/general/logout') {
        return Promise.reject(error);
    }

    // Check if we are dealing with a 403 error = Forbidden when trying to refresh the token
    if (status === 403 && originalRequest.url === '/general/oauth/token' && !originalRequest.login) {
        console.log('403 error when trying to refresh token');

        await logout({ navigate: false, reason: LogoutReason.SESSION_EXPIRED });

        return Promise.reject(error);
    }

    // Check if we are dealing with a 401 error = Unauthorized
    if (status === 401) {
        if (
            !originalRequest._retry &&
            originalRequest.url !== '/general/oauth/token' &&
            originalRequest.url !== '/general/oauth/confirm'
        ) {
            console.log('Trying to refresh token due to 401 error');

            originalRequest._retry = true;

            // Try to refresh the token — abort retry if no token was returned or refresh threw
            const newAuthHeader = await refreshExpiredToken();

            if (!newAuthHeader) {
                return Promise.reject(error);
            }

            originalRequest.headers['Authorization'] = newAuthHeader;

            // Ensure queue is resumed after token refresh (safety measure)
            resumeQueue();

            // Retry the original request after refresh
            return api.request(originalRequest);
        }

        if (!originalRequest.login) {
            useToast().error(i18n.global.t('error.expired_session'));

            console.log('Logging out due to 401 error');

            // Logout if we are still unauthorized and redirect to login
            await logout({
                navigate: originalRequest.url !== '/general/oauth/token',
                reason: LogoutReason.SESSION_EXPIRED,
            });
        }

        return Promise.reject(error);
    }

    // Catch blob responses and parse them as JSON
    if (
        error.request?.responseType === 'blob' &&
        error.response?.data instanceof Blob &&
        error.response.data.type &&
        error.response.data.type.toLowerCase().indexOf('json') !== -1
    ) {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onload = () => {
                try {
                    error.response.data = JSON.parse(reader.result);
                } catch (parseError) {
                    console.error('Failed to parse blob as JSON:', parseError);
                }
                resolve(Promise.reject(error));
            };

            reader.onerror = () => {
                reject(error);
            };

            reader.readAsText(error.response.data);
        });
    }

    return Promise.reject(error);
}

api.defaults.showLoader = true;

api.interceptors.request.use(onRequest, handleRequestError);
api.interceptors.response.use(onResponse, handleResponseError);

export default api;

/**
 * Resume the request queue if paused
 *
 * @returns {void}
 */
export function resumeQueue() {
    if (!queue.isPaused) {
        return;
    }

    queue.start();
}
